import { Injectable } from '@angular/core';
import { CountryDepartment } from '@app/models/country-department';
import { CountryDepartmentService } from '@app/services/api/country-department.service';
import { FlashService } from '@app/services/flash.service';
import {
	CountryDepartmentActionTypes,
	Create,
	CreateFail,
	CreateSuccess,
	Delete,
	DeleteFail,
	DeleteSuccess,
	Load,
	LoadById,
	LoadByIdFail,
	LoadByIdSuccess,
	LoadFail,
	LoadPlanner,
	LoadPlannerFail,
	LoadPlannerSuccess,
	LoadSuccess,
	LoadbyProduct,
	LoadbyProductFail,
	LoadbyProductSuccess,
	Search,
	SearchFail,
	SearchSuccess,
	Update,
	UpdateFail,
	UpdateSuccess,
	Void,
	updateHierarchy,
	updateHierarchyFail,
	updateHierarchySuccess
} from '@core/actions/country-department.action';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';

@Injectable()
export class CountryDepartmentEffects {
	@Effect()
	load$: Observable<Action> = this.actions$.pipe(
		ofType<Load>(CountryDepartmentActionTypes.Load),
		map(action => [action.FollowId, action.dataFilter]),
		switchMap(([FollowId, dataFilter]) =>
			this.countryDepartmentsApi.get(FollowId, dataFilter).pipe(
				map(
					(countryDepartments: CountryDepartment[]) =>
						new LoadSuccess(countryDepartments)
				),
				catchError(error => of(new LoadFail(error)))
			)
		)
	);

	@Effect()
	loadPlanner$: Observable<Action> = this.actions$.pipe(
		ofType<LoadPlanner>(CountryDepartmentActionTypes.LoadPlanner),
		map(action => [action.FollowId, action.dataFilter]),
		switchMap(([FollowId, dataFilter]) =>
			this.countryDepartmentsApi.get(FollowId, dataFilter).pipe(
				map(
					(countryDepartments: CountryDepartment[]) =>
						new LoadPlannerSuccess(countryDepartments)
				),
				catchError(error => of(new LoadPlannerFail(error)))
			)
		)
	);

	@Effect()
	loadByProduct$: Observable<Action> = this.actions$.pipe(
		ofType<LoadbyProduct>(CountryDepartmentActionTypes.LoadbyProduct),
		map(action => [action.FollowId, action.dataFilter]),
		switchMap(([FollowId, dataFilter]) =>
			this.countryDepartmentsApi.getByProducts(FollowId, dataFilter).pipe(
				map(
					(countryDepartments: CountryDepartment[]) =>
						new LoadbyProductSuccess(countryDepartments)
				),
				catchError(error => of(new LoadbyProductFail(error)))
			)
		)
	);

	@Effect()
	search$: Observable<Action> = this.actions$.pipe(
		ofType<Search>(CountryDepartmentActionTypes.Search),
		map(action => [action.data, action.FollowId]),
		switchMap(([data, FollowId]) =>
			this.countryDepartmentsApi.search(data, FollowId).pipe(
				map(
					(countryDepartmentsIds: any[]) =>
						new SearchSuccess(countryDepartmentsIds)
				),
				catchError(error => of(new SearchFail(error)))
			)
		)
	);


	@Effect()
	create$: Observable<Action> = this.actions$.pipe(
		ofType<Create>(CountryDepartmentActionTypes.Create),
		map(action => action.data),
		switchMap(data =>
			this.countryDepartmentsApi.create(data).pipe(
				map((countryDepartment: CountryDepartment) => new CreateSuccess(countryDepartment)),
				catchError(error => of(new CreateFail(error)))
			)
		)
	);

	@Effect()
	createSuccess$: Observable<Action> = this.actions$.pipe(
		ofType<CreateSuccess>(CountryDepartmentActionTypes.CreateSuccess),
		map(action => {
			this.fs.emit({ type: 'success', message: 'JC creado con exito' });
			return new Void()
		})
	);

	@Effect()
	update$: Observable<Action> = this.actions$.pipe(
		ofType<Update>(CountryDepartmentActionTypes.Update),
		map(action => [action.id, action.countryDepartment]),
		concatMap(([id, countryDepartment]) =>
			this.countryDepartmentsApi.update(id, countryDepartment).pipe(
				map((countryDepartment: CountryDepartment) => { return new UpdateSuccess(id, countryDepartment) }),
				catchError(error => of(new UpdateFail(error)))
			)
		)
	);

	@Effect()
	updateSuccess$: Observable<Action> = this.actions$.pipe(
		ofType<UpdateSuccess>(CountryDepartmentActionTypes.UpdateSuccess),
		map(action => {
			this.fs.emit({ type: 'success', message: 'JC actualizado con exito' });
			return new Void()
		})
	);

	@Effect()
	updateFail$ = this.actions$.pipe(
		ofType<UpdateFail>(CountryDepartmentActionTypes.UpdateFail),
		map(action => {
			this.fs.emit({ type: 'danger', message: 'Error al actualizar' });

			return new Void();
		})
	);

	@Effect()
	delete$: Observable<Action> = this.actions$.pipe(
		ofType<Delete>(CountryDepartmentActionTypes.Delete),
		map(action => action.id),
		concatMap(id =>
			this.countryDepartmentsApi.destroySingle(id).pipe(
				concatMap(res => {
					this.fs.emit({
						type: 'success',
						message: 'JC eliminado exitosamente'
					});
					return [new DeleteSuccess(id)];
				}),
				catchError(error => of(new DeleteFail(error)))
			)
		)
	);



	@Effect()
	deleteFail$: Observable<Action> = this.actions$.pipe(
		ofType<DeleteFail>(CountryDepartmentActionTypes.DeleteFail),
		map(action => {
			this.fs.emit({ type: 'danger', message: 'Error al eliminar' });
			return new Void()
		})
	);

	@Effect()
	loadById$: Observable<Action> = this.actions$.pipe(
		ofType<LoadById>(CountryDepartmentActionTypes.LoadById),
		map(action => [action.FollowId, action.dataFilter]),
		switchMap(([FollowId, dataFilter]) =>
			this.countryDepartmentsApi.getById(FollowId, dataFilter).pipe(
				map(
					(countryDepartments: CountryDepartment[]) =>
						new LoadByIdSuccess(countryDepartments[0].id, {
							quantity: countryDepartments[0].quantity,
							totalUs: countryDepartments[0].totalUs,
						})
				),
				catchError(error => of(new LoadByIdFail(error)))
			)
		)
	);

	@Effect()
	updateHierarchyPlanner$: Observable<Action> = this.actions$.pipe(
		ofType<updateHierarchy>(CountryDepartmentActionTypes.updateHierarchy),
		switchMap(() =>
			this.countryDepartmentsApi.updateHierarchy().pipe(
				map((departments: any) => new updateHierarchySuccess()),
				catchError(error => of(new updateHierarchyFail(error)))
			)
		)
	);

	@Effect({ dispatch: false })
	updateHierarchyPlannerSuccess$ = this.actions$.pipe(
    	ofType<updateHierarchySuccess>(CountryDepartmentActionTypes.updateHierarchySuccess),
    	map(action => action.data),
		tap(res =>
			this.fs.emit({
				message: 'Jerarquías sincronizadas con éxito!',
				type: 'success'
			})
		)
  	);

	@Effect({ dispatch: false })
	updateHierarchyPlannerFail$ = this.actions$.pipe(
		ofType<updateHierarchyFail>(CountryDepartmentActionTypes.updateHierarchyFail),
		map(action => action.error),
		tap(res =>
			this.fs.emit({
				message: 'No se pudo sincronizar las Jerarquías',
				type: 'danger'
			})
		)
	);

	constructor(
		private actions$: Actions,
		private countryDepartmentsApi: CountryDepartmentService,
		private fs: FlashService
	) { }
}
